import { useContext, useEffect, useRef } from 'react';
import { PinAuth, RestaurantContext } from 'Contexts/RestaurantContext';
import { AuthContext } from 'Contexts/AuthContext';
import { MAX_WAIT } from 'CustomHooks/useAfterRolesLoaded';
import {
  ActivityRecord,
  SecurityActivityContext,
} from 'Contexts/SecurityActivityContext';
import {
  ActivityCategory,
  ActivityDetails,
  ActivityOutcome,
  AuthorizationMethod,
  profileOf,
  randomId,
  reasonOf,
} from 'utils/securityActivity/events';
import { GateActivity, SecurityGrant } from 'utils/securityActivity/grant';

export type { GateActivity, SecurityGrant, TrackDetails } from 'utils/securityActivity/grant';
export { trackChange, unlessRefused } from 'utils/securityActivity/grant';

const OVERRIDES = [
  'reservations.moreThanOnWebsite.write',
  'reservations.reservationLength.write',
  'reservations.blockedTime.write',
];

const defaultCategory = (type: string): Exclude<ActivityCategory, 'authorization'> => {
  if (type.startsWith('dashboard.') && type.endsWith('.read')) return 'view';
  if (type === 'dashboard.printReservationList.write') return 'print';
  if (type === 'calendar.newShift.write') return 'form';
  if (OVERRIDES.includes(type)) return 'override';
  // Protected occassions are configured under their own id.
  if (!/^(dashboard|calendar|reservations)\./.test(type)) return 'override';
  return 'change';
};

const bestEffort = (log: () => void) => {
  try {
    log();
  } catch (error) {
    console.error('security activity not recorded', error);
  }
};

type UsePinAuth = () => {
  authenticate: (
    type: SecurityPlusKeys,
    onSubmit: (signature: string, grant?: SecurityGrant) => void | Promise<void>,
    onDenial?: () => void | Promise<void>,
    activity?: GateActivity
  ) => void;
  checkAuthentication: (
    type: SecurityPlusKeys,
    ignoreSession?: boolean
  ) => boolean;
  /** Security Plus protects this right with at least one PIN (admin exemption aside). */
  isGated: (type: SecurityPlusKeys) => boolean;
};

const usePinAuth: UsePinAuth = () => {
  const {
    pinAuth,
    securityPlus,
    securityPlusLoaded,
    storedPins,
    storedPinSessions,
    experimentalFlags,
    restaurantId,
  } = useContext(RestaurantContext);

  const { isAdmin, uid } = useContext(AuthContext);
  const { record } = useContext(SecurityActivityContext);

  // Until the restaurant's Security Plus settings and the admin exemption flag
  // have loaded, nobody knows what is protected: that is not "unprotected".
  const policyUnknown = securityPlusLoaded === false;
  const intentScope = `${uid}|${restaurantId}`;

  function getSignatures(type: SecurityPlusKeys) {
    if (type === 'none') return null;

    // return ['LD'];

    if (!securityPlus?.active || !securityPlus?.settings) return null;

    let x: any = securityPlus.settings.rights[type];

    if (!Array.isArray(x)) return null;

    let pins: string[] = [];

    for (const key of x) {
      if (securityPlus.settings.signatures[key]) {
        pins.push(key);
      } else {
        let rightGroups = securityPlus.settings.rightGroups.find(
          (x) => x.id === key
        );

        if (rightGroups?.signatures) {
          pins.push(...rightGroups.signatures);
        }
      }
    }

    return pins;
  }

  function checkAuthentication(type: SecurityPlusKeys, ignoreSession = true) {
    // Callers that only ask route the action through authenticate, which waits.
    if (policyUnknown) return type !== 'none';

    if (experimentalFlags?.data?.disableSecurityForAdmins && isAdmin)
      return false;

    let signatures = getSignatures(type);

    if (
      !ignoreSession &&
      !!signatures?.some((x) => storedPins[x] && storedPins[x] > Date.now())
    )
      return false;

    return !!signatures?.length;
  }

  const isGated = (type: SecurityPlusKeys) =>
    policyUnknown ? type !== 'none' : !!getSignatures(type)?.length;

  function authenticateNow(
    type: SecurityPlusKeys,
    onSubmit: (signature: string, grant?: SecurityGrant) => void | Promise<void>,
    onDenial?: () => void | Promise<void>,
    activity: GateActivity = {}
  ) {
    const signatures = getSignatures(type);
    const category = activity.category ?? defaultCategory(type);
    const action = activity.action ?? type;
    const authorizationId = randomId();
    const base = { permission: type, authorizationId };

    const grantFor = (
      method: AuthorizationMethod,
      initials: string | null,
      pinSessionId: string | null
    ): SecurityGrant => {
      const pinProfile = profileOf(initials);
      const by = { ...base, method, pinProfile, pinSessionId };
      const grantRecord: SecurityGrant['record'] = (recordCategory, recordAction, outcome, details) =>
        bestEffort(() =>
          record({ ...by, ...details, category: recordCategory, action: recordAction, outcome })
        );

      grantRecord(category === 'change' ? 'authorization' : category, action, 'succeeded', {
        targets: activity.targets,
        changes: activity.changes,
      });

      return {
        permission: type,
        method,
        pinProfile,
        authorizationId,
        record: grantRecord,
        track: (trackedAction, write, { fromResult, ...details } = {}) =>
          // The write's own result and error pass through unchanged, whatever logging does.
          write.then(
            (result) => {
              bestEffort(() => {
                const derived: ActivityDetails & { outcome?: ActivityOutcome } = fromResult
                  ? fromResult(result)
                  : {};
                const { outcome = 'succeeded', ...fromWrite } = derived;
                grantRecord('change', trackedAction, outcome, { ...details, ...fromWrite });
              });
              return result;
            },
            (error) => {
              bestEffort(() =>
                grantRecord('change', trackedAction, 'failed', {
                  ...details,
                  reason: reasonOf(error),
                })
              );
              throw error;
            }
          ),
      };
    };

    const attempt = (
      activityRecord: Omit<ActivityRecord, 'permission' | 'authorizationId' | 'category' | 'action'>
    ) =>
      bestEffort(() =>
        record({ ...base, ...activityRecord, category: 'authorization', action })
      );

    if (experimentalFlags?.data?.disableSecurityForAdmins && isAdmin) {
      // Only an exemption where Security Plus would have asked for a PIN.
      onSubmit('', signatures?.length ? grantFor('admin_exemption', null, null) : undefined);
      return;
    }

    let signature = signatures?.find(
      (x) => storedPins[x] && storedPins[x] > Date.now()
    );

    if (signature) {
      onSubmit(
        signature,
        grantFor('pin_session', signature, storedPinSessions?.[signature] ?? null)
      );
      return;
    }

    if (!signatures?.length) {
      onSubmit('');
    } else {
      let wrongPins = 0;

      pinAuth({
        onSubmit: (signature, session) =>
          onSubmit(
            signature,
            grantFor('pin_entered', signature, session?.sessionId ?? null)
          ),
        // A wrong PIN names only the profile that was picked, not who typed it.
        onWrongPin: (initials) => {
          wrongPins += 1;
          attempt({
            outcome: 'failed',
            reason: 'wrong_pin',
            method: 'pin_entered',
            pinProfile: profileOf(initials),
          });
        },
        signatures,
        type,
        onClose: (reason) => {
          attempt({
            outcome: 'cancelled',
            reason: reason ?? (wrongPins >= 3 ? 'too_many_attempts' : 'dialog_closed'),
            method: null,
            pinProfile: null,
          });
          onDenial?.();
        },
      });
    }
  }

  // An action asked for while the settings load waits for them and then goes
  // through the gate as it is by then. One of another login or restaurant, or
  // one waiting longer than a user would, is not taken.
  type Intent = { scope: string; at: number; args: Parameters<typeof authenticateNow> };
  const waiting = useRef<Intent | null>(null);
  const expiry = useRef<number | null>(null);
  const current = useRef(authenticateNow);
  current.current = authenticateNow;

  const takeWaiting = () => {
    const intent = waiting.current;
    waiting.current = null;
    if (expiry.current !== null) window.clearTimeout(expiry.current);
    expiry.current = null;
    return intent;
  };

  // A policy read that fails never ends the wait, so the timer denies it.
  const denyWaiting = () => takeWaiting()?.args[2]?.();

  useEffect(() => () => void takeWaiting(), []);

  useEffect(() => {
    const intent = waiting.current;
    if (!intent) return;
    if (intent.scope !== intentScope || Date.now() - intent.at > MAX_WAIT) {
      denyWaiting();
      return;
    }
    if (policyUnknown) return;
    takeWaiting();
    current.current(...intent.args);
  }, [policyUnknown, intentScope]);

  const authenticate: typeof authenticateNow = (...args) => {
    if (policyUnknown && args[0] !== 'none') {
      denyWaiting();
      waiting.current = { scope: intentScope, at: Date.now(), args };
      expiry.current = window.setTimeout(denyWaiting, MAX_WAIT);
      return;
    }
    authenticateNow(...args);
  };

  return {
    authenticate,
    checkAuthentication,
    isGated,
  };
};

export default usePinAuth;

export type SecurityPlusKeys =
  | 'dashboard.statistics.read'
  | 'dashboard.calendar.read'
  | 'dashboard.guestBook.read'
  | 'dashboard.feedback.read'
  | 'dashboard.sia.read'
  | 'dashboard.delivery.read'
  | 'dashboard.menu.read'
  | 'dashboard.voucher.read'
  | 'dashboard.settings.read'
  | 'dashboard.bookkeeping.read'
  | 'dashboard.printReservationList.write'
  | 'calendar.newShift.write'
  | 'calendar.blockReservations.write'
  | 'calendar.editShift.write'
  | 'calendar.deleteShift.write'
  | 'calendar.addNote.write'
  | 'calendar.editNote.write'
  | 'reservations.newReservation.write'
  | 'reservations.cancelReservation.write'
  | 'reservations.editReservation.write'
  | 'reservations.moreThanOnWebsite.write'
  | 'reservations.reservationLength.write'
  | 'reservations.chooseTableThatIsNotRecommended.write'
  | 'reservations.walkin.read'
  | 'reservations.walkin.write'
  | 'reservations.hostComment.read'
  | 'reservations.hostComment.write'
  | 'reservations.occassions.[occassionId].read'
  | 'reservations.occassions.[occassionId].write'
  | 'reservations.blockedTime.read'
  | 'reservations.blockedTime.write'
  | 'reservations.spaceBlocked'
  | 'reservations.tableBlocked'
  | 'none';
